import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// 🌐 PUBLIC CLUB PAGE - Pagina pubblica del club (Design Dark Mode)
const PublicClubPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeClassifica, setActiveClassifica] = useState('stagione');
  const [copied, setCopied] = useState(false);
  const [instagramCopied, setInstagramCopied] = useState(false);

  useEffect(() => {
    if (username) loadPublicClub();
  }, [username]);

  const loadPublicClub = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/club/${username}`);
      if (response.ok) {
        const data = await response.json();
        setClubData(data);
      } else if (response.status === 403) {
        setError('Questo club è privato');
      } else if (response.status === 404) {
        setError('Club non trovato');
      } else {
        setError('Errore nel caricamento');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  // Generate social share URL with dynamic OG tags
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const socialShareUrl = `${baseUrl}/api/s/club/${username}`;
  const directUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  // Note: The redirect URL should go to /club/public/{username}
  // This is handled by the backend router

  const handleShare = async (platform) => {
    const text = `Guarda le statistiche del club ${clubData?.club?.name} su My Subbuteo Manager!`;
    // Use social URL for sharing (has OG meta tags)
    const urlToShare = socialShareUrl;
    
    switch(platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + urlToShare)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(urlToShare)}&quote=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'instagram':
        try {
          await navigator.clipboard.writeText(urlToShare);
        } catch (e) {
          // Fallback se clipboard non disponibile
          console.log('Clipboard non disponibile');
        }
        setInstagramCopied(true);
        setTimeout(() => setInstagramCopied(false), 3000);
        // Apri Instagram se su mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          setTimeout(() => {
            window.open('instagram://app', '_blank');
          }, 500);
        }
        break;
      case 'copy':
        navigator.clipboard.writeText(urlToShare);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
          <p className="mt-4 text-gray-400">Caricamento club...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="text-center">
          <div className="text-6xl mb-4">{error.includes('privato') ? '🔒' : '🚫'}</div>
          <h1 className="text-2xl font-bold text-white mb-2">{error}</h1>
          <p className="text-gray-400 mb-6">
            {error.includes('privato') ? 'Solo i soci registrati possono accedere a questo club.' : 'Controlla l\'URL e riprova.'}
          </p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700"
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }

  if (!clubData) return null;

  const { club, stats, classifiche, tornei_recenti } = clubData;
  
  // Get customization colors with fallbacks
  const primaryColor = club.primary_color || '#3B82F6';
  const secondaryColor = club.secondary_color || '#1E40AF';
  const fullLogoUrl = club.logo_url ? (club.logo_url.startsWith('http') ? club.logo_url : `${BACKEND_URL}${club.logo_url}`) : '';
  const fullBgUrl = club.background_image_url ? (club.background_image_url.startsWith('http') ? club.background_image_url : `${BACKEND_URL}${club.background_image_url}`) : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header with customization */}
      <header 
        className="relative shadow-2xl overflow-hidden"
        style={{
          background: fullBgUrl 
            ? 'transparent' 
            : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
        }}
      >
        {/* Background Image */}
        {fullBgUrl && (
          <>
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${fullBgUrl})` }}
            />
            {club.background_blur && (
              <div 
                className="absolute inset-0" 
                style={{ 
                  backdropFilter: 'blur(8px)',
                  backgroundColor: `rgba(0,0,0,${(100 - (club.background_opacity || 100)) / 100})`
                }}
              />
            )}
            {!club.background_blur && (club.background_opacity || 100) < 100 && (
              <div 
                className="absolute inset-0" 
                style={{ 
                  backgroundColor: `rgba(0,0,0,${(100 - (club.background_opacity || 100)) / 100})`
                }}
              />
            )}
            {/* Color overlay for readability */}
            <div 
              className="absolute inset-0"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor}80 0%, ${secondaryColor}80 100%)`
              }}
            />
          </>
        )}
        
        <div className="relative max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-5">
            {/* Logo/Avatar */}
            <div className="relative">
              {fullLogoUrl ? (
                <img 
                  src={fullLogoUrl} 
                  alt={`Logo ${club.name}`}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white/30 shadow-xl"
                  data-testid="public-club-logo"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-white/20 border-4 border-white/30 flex items-center justify-center shadow-xl">
                  <span className="text-4xl">🏢</span>
                </div>
              )}
              <div 
                className="absolute -bottom-1 -right-1 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg"
                style={{ backgroundColor: secondaryColor }}
              >
                🌐 Pubblico
              </div>
            </div>
            
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">{club.name}</h1>
              {club.city && (
                <p className="text-white/80 text-sm mt-1 drop-shadow">📍 {club.city}</p>
              )}
              {club.foundation_year && (
                <p className="text-white/60 text-xs mt-1">Fondato nel {club.foundation_year}</p>
              )}
              {/* Social Links */}
              {(club.website || club.instagram || club.facebook) && (
                <div className="flex gap-3 mt-3">
                  {club.website && (
                    <a 
                      href={club.website.startsWith('http') ? club.website : `https://${club.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white text-sm"
                      data-testid="public-club-website"
                    >
                      🌐 Sito
                    </a>
                  )}
                  {club.instagram && (
                    <a 
                      href={club.instagram.startsWith('http') ? club.instagram : `https://instagram.com/${club.instagram.replace('@', '')}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white text-sm"
                      data-testid="public-club-instagram"
                    >
                      📸 Instagram
                    </a>
                  )}
                  {club.facebook && (
                    <a 
                      href={club.facebook.startsWith('http') ? club.facebook : `https://facebook.com/${club.facebook}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white text-sm"
                      data-testid="public-club-facebook"
                    >
                      📘 Facebook
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        
        {/* Stats principali */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/30 backdrop-blur rounded-2xl p-5 text-center border border-yellow-500/30">
            <div className="text-4xl font-bold text-yellow-400">{stats.tornei_completati}</div>
            <div className="text-yellow-300 text-sm mt-1">Tornei</div>
          </div>
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-5 text-center border border-gray-700/50">
            <div className="text-4xl font-bold text-white">{stats.partite_totali}</div>
            <div className="text-gray-400 text-sm mt-1">Partite</div>
          </div>
          <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 backdrop-blur rounded-2xl p-5 text-center border border-purple-500/30">
            <div className="text-4xl font-bold text-purple-400">{stats.gol_totali}</div>
            <div className="text-purple-300 text-sm mt-1">Gol Totali</div>
          </div>
        </div>

        {/* Classifiche */}
        {(classifiche?.stagione?.length > 0 || classifiche?.gol?.length > 0) && (
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl overflow-hidden border border-gray-700/50">
            {/* Tab Classifiche */}
            <div className="flex border-b border-gray-700">
              <button 
                onClick={() => setActiveClassifica('stagione')}
                className={`flex-1 py-4 px-4 text-sm font-medium transition-all ${
                  activeClassifica === 'stagione' 
                    ? 'bg-blue-600/30 text-blue-300 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                🏆 Classifica Stagionale
              </button>
              <button 
                onClick={() => setActiveClassifica('gol')}
                className={`flex-1 py-4 px-4 text-sm font-medium transition-all ${
                  activeClassifica === 'gol' 
                    ? 'bg-purple-600/30 text-purple-300 border-b-2 border-purple-400' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                ⚽ Capocannonieri
              </button>
            </div>
            
            {/* Lista Classifica */}
            <div className="p-4">
              {(activeClassifica === 'stagione' ? classifiche.stagione : classifiche.gol).map((s, i) => (
                <div 
                  key={i} 
                  className={`flex items-center justify-between py-3 px-4 rounded-xl mb-2 ${
                    i === 0 ? 'bg-yellow-500/20 border border-yellow-500/30' :
                    i === 1 ? 'bg-gray-500/20 border border-gray-500/30' :
                    i === 2 ? 'bg-orange-500/20 border border-orange-500/30' :
                    'bg-black/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                      i === 0 ? 'bg-yellow-400 text-yellow-900' : 
                      i === 1 ? 'bg-gray-300 text-gray-700' : 
                      i === 2 ? 'bg-orange-400 text-orange-900' : 
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : s.pos}
                    </span>
                    <span className="font-medium text-white">{s.nome}</span>
                  </div>
                  <span className={`font-bold text-lg ${
                    activeClassifica === 'stagione' ? 'text-blue-400' : 'text-purple-400'
                  }`}>
                    {activeClassifica === 'stagione' ? `${s.punti} pts` : `${s.gol} ⚽`}
                  </span>
                </div>
              ))}
              {(activeClassifica === 'stagione' ? classifiche.stagione : classifiche.gol).length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tornei Recenti */}
        {tornei_recenti && tornei_recenti.length > 0 && (
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-gray-700/50" data-testid="tornei-recenti">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="text-2xl">🏆</span> Ultimo Torneo Concluso
            </h3>
            <div className="space-y-4">
              {/* Mostra solo il primo torneo (ultimo concluso) con podio completo */}
              {tornei_recenti.slice(0, 1).map((t, i) => (
                <div 
                  key={i} 
                  className="p-5 bg-gradient-to-br from-gray-900/50 to-gray-800/50 rounded-xl border border-gray-700/30"
                  data-testid="ultimo-torneo"
                >
                  <div className="mb-4">
                    <p className="font-bold text-xl text-white">{t.nome}</p>
                    <p className="text-sm text-gray-400">
                      {t.formato?.replace(/_/g, ' ')} • {t.partecipanti} partecipanti
                    </p>
                  </div>
                  
                  {/* Podio */}
                  {(t.podio?.primo || t.vincitore) && (
                    <div className="flex items-end justify-center gap-4 mt-4" data-testid="podio">
                      {/* 2° Posto */}
                      {t.podio?.secondo && (
                        <div className="text-center">
                          <div className="w-20 h-16 bg-gray-500/30 rounded-t-lg flex items-end justify-center pb-2 border-t-4 border-gray-400">
                            <span className="text-2xl">🥈</span>
                          </div>
                          <div className="bg-gray-600/50 px-3 py-2 rounded-b-lg">
                            <p className="text-gray-300 text-sm font-medium truncate max-w-[80px]">{t.podio.secondo}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* 1° Posto */}
                      <div className="text-center">
                        <div className="w-24 h-24 bg-yellow-500/30 rounded-t-lg flex items-end justify-center pb-2 border-t-4 border-yellow-400">
                          <span className="text-4xl">🥇</span>
                        </div>
                        <div className="bg-yellow-600/50 px-4 py-2 rounded-b-lg">
                          <p className="text-yellow-200 font-bold truncate max-w-[96px]">{t.podio?.primo || t.vincitore}</p>
                        </div>
                      </div>
                      
                      {/* 3° Posto */}
                      {t.podio?.terzo && (
                        <div className="text-center">
                          <div className="w-20 h-12 bg-orange-500/30 rounded-t-lg flex items-end justify-center pb-2 border-t-4 border-orange-400">
                            <span className="text-xl">🥉</span>
                          </div>
                          <div className="bg-orange-600/50 px-3 py-2 rounded-b-lg">
                            <p className="text-orange-200 text-sm font-medium truncate max-w-[80px]">{t.podio.terzo}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Altri tornei (senza podio, solo vincitore) */}
              {tornei_recenti.length > 1 && (
                <div className="mt-4 pt-4 border-t border-gray-700/50">
                  <p className="text-gray-500 text-sm mb-3">Altri tornei recenti</p>
                  {tornei_recenti.slice(1).map((t, i) => (
                    <div 
                      key={i} 
                      className="flex items-center justify-between py-2 px-3 bg-black/20 rounded-lg mb-2"
                    >
                      <span className="text-gray-300 text-sm">{t.nome}</span>
                      {t.vincitore && (
                        <span className="text-yellow-400 text-sm">🏆 {t.vincitore}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pulsanti Social */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-gray-700/50">
          <h3 className="text-white font-bold mb-4 text-center">📤 Condividi questo club</h3>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => handleShare('whatsapp')}
              className="flex items-center gap-2 px-5 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-all shadow-lg"
              data-testid="club-share-whatsapp"
            >
              <span className="text-xl">📱</span> WhatsApp
            </button>
            <button
              onClick={() => handleShare('facebook')}
              className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all shadow-lg"
              data-testid="club-share-facebook"
            >
              <span className="text-xl">📘</span> Facebook
            </button>
            <button
              onClick={() => handleShare('instagram')}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all shadow-lg ${
                instagramCopied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
              }`}
              data-testid="club-share-instagram"
            >
              <span className="text-xl">{instagramCopied ? '✅' : '📸'}</span> 
              {instagramCopied ? 'Link copiato!' : 'Instagram'}
            </button>
            <button
              onClick={() => handleShare('copy')}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all shadow-lg ${
                copied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-600 text-white hover:bg-gray-500'
              }`}
              data-testid="club-share-copy"
            >
              <span className="text-xl">{copied ? '✅' : '🔗'}</span> 
              {copied ? 'Copiato!' : 'Copia Link'}
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-6 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Vuoi unirti a questo club?</h3>
          <p className="text-blue-200 mb-4">Contatta un amministratore per ottenere il codice di iscrizione</p>
          <button 
            onClick={() => navigate('/login')}
            className="px-8 py-3 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition-all shadow-lg"
          >
            Accedi / Registrati
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">My Subbuteo Manager - Il gestionale digitale per il Subbuteo</p>
        </div>
      </footer>
    </div>
  );
};

// REPLACED_OLD_ATTIVA_CODICE_START
const AttivaCodicePage_OLD = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [codeInfo, setCodeInfo] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Step: 'input' | 'verified' | 'login' | 'register'
  const [step, setStep] = useState('input');
  
  // Form registrazione
  const [regForm, setRegForm] = useState({ email: '', password: '', confirmPassword: '', name: '' });
  const [regLoading, setRegLoading] = useState(false);
  
  // Form login
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Check se utente già loggato
  const existingToken = localStorage.getItem('token');
  const [isLoggedIn] = useState(!!existingToken);
  
  const verifyCode = async () => {
    if (!code.trim()) {
      setError('Inserisci il codice');
      return;
    }
    
    setVerifying(true);
    setError('');
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/verify-club-code/${code.trim().toUpperCase()}`);
      const data = await res.json();
      
      if (data.valid) {
        setCodeInfo(data);
        setStep('verified');
        // Pre-compila il nome se disponibile
        if (data.member_name) {
          setRegForm(prev => ({ ...prev, name: data.member_name }));
        }
      } else {
        setError(data.error || 'Codice non valido');
      }
    } catch (err) {
      setError('Errore di connessione');
    }
    
    setVerifying(false);
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (regForm.password !== regForm.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }
    
    if (regForm.password.length < 6) {
      setError('Password minimo 6 caratteri');
      return;
    }
    
    setRegLoading(true);
    setError('');
    
    try {
      // 1. Registra utente
      const regRes = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: regForm.email,
          password: regForm.password,
          name: regForm.name
        })
      });
      
      const regData = await regRes.json();
      
      if (!regRes.ok) {
        setError(regData.error || 'Errore durante la registrazione');
        setRegLoading(false);
        return;
      }
      
      // 2. Attiva il codice
      const activateRes = await fetch(`${BACKEND_URL}/api/activate-club-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${regData.token}`
        },
        body: JSON.stringify({ code: code.trim().toUpperCase() })
      });
      
      const activateData = await activateRes.json();
      
      if (activateRes.ok) {
        // Salva il token
        localStorage.setItem('subbuteo_token', regData.token);
        setSuccess(`🎉 Benvenuto nel club ${codeInfo.club_name}!`);
        
        // Redirect dopo 2 secondi
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(activateData.error || 'Errore attivazione codice');
      }
    } catch (err) {
      setError('Errore di connessione');
    }
    
    setRegLoading(false);
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError('');
    
    try {
      // 1. Login
      const loginRes = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password
        })
      });
      
      const loginData = await loginRes.json();
      
      if (!loginRes.ok) {
        setError(loginData.error || 'Credenziali non valide');
        setLoginLoading(false);
        return;
      }
      
      // 2. Attiva il codice
      const activateRes = await fetch(`${BACKEND_URL}/api/activate-club-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${loginData.token}`
        },
        body: JSON.stringify({ code: code.trim().toUpperCase() })
      });
      
      const activateData = await activateRes.json();
      
      if (activateRes.ok) {
        localStorage.setItem('subbuteo_token', loginData.token);
        setSuccess(`🎉 Sei entrato nel club ${codeInfo.club_name}!`);
        
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(activateData.error || 'Errore attivazione codice');
      }
    } catch (err) {
      setError('Errore di connessione');
    }
    
    setLoginLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🎟️</div>
          <h1 className="text-2xl font-bold text-gray-800">Attiva Codice Club</h1>
          <p className="text-gray-500">Hai ricevuto un codice dal tuo club? Attivalo qui!</p>
        </div>
        
        {/* Messages */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}
        
        {/* Step 1: Input codice */}
        {step === 'input' && (
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Inserisci il codice (es: ABC123)"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 border rounded-lg text-center text-xl tracking-widest uppercase"
              maxLength={10}
            />
            <button
              onClick={verifyCode}
              disabled={verifying}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              {verifying ? 'Verifica in corso...' : '🔍 Verifica Codice'}
            </button>
          </div>
        )}
        
        {/* Step 2: Codice verificato - scegli login o registrati */}
        {step === 'verified' && codeInfo && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-green-600 text-lg font-semibold mb-1">✅ Codice valido!</div>
              <div className="text-gray-800 font-bold text-xl">{codeInfo.club_name}</div>
              {codeInfo.member_name && (
                <div className="text-gray-500 text-sm mt-1">
                  Invito per: {codeInfo.member_name}
                </div>
              )}
            </div>
            
            {isLoggedIn ? (
              <button
                onClick={() => handleLogin({ preventDefault: () => {} })}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                🚀 Attiva con il mio account
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setStep('login')}
                  className="bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
                >
                  🔐 Accedi
                </button>
                <button
                  onClick={() => setStep('register')}
                  className="bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700"
                >
                  ✨ Registrati
                </button>
              </div>
            )}
            
            <button
              onClick={() => { setStep('input'); setCodeInfo(null); setCode(''); }}
              className="w-full text-gray-500 text-sm"
            >
              ← Inserisci un altro codice
            </button>
          </div>
        )}
        
        {/* Step 3a: Login */}
        {step === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center text-sm text-blue-700">
              Accedi per entrare in <strong>{codeInfo?.club_name}</strong>
            </div>
            
            <input
              type="email"
              placeholder="Email"
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg"
              required
            />
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {loginLoading ? 'Accesso in corso...' : '🔐 Accedi e Attiva'}
            </button>
            <button
              type="button"
              onClick={() => setStep('verified')}
              className="w-full text-gray-500 text-sm"
            >
              ← Indietro
            </button>
          </form>
        )}
        
        {/* Step 3b: Registrazione */}
        {step === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center text-sm text-green-700">
              Registrati per entrare in <strong>{codeInfo?.club_name}</strong>
            </div>
            
            <input
              type="text"
              placeholder="Nome completo"
              value={regForm.name}
              onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg"
              required
            />
            <input
              type="email"
              placeholder="Email"
              value={regForm.email}
              onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg"
              required
            />
            <input
              type="password"
              placeholder="Password (min 6 caratteri)"
              value={regForm.password}
              onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg"
              required
            />
            <input
              type="password"
              placeholder="Conferma password"
              value={regForm.confirmPassword}
              onChange={(e) => setRegForm({ ...regForm, confirmPassword: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg"
              required
            />
            <button
              type="submit"
              disabled={regLoading}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              {regLoading ? 'Registrazione in corso...' : '✨ Registrati e Attiva'}
            </button>
            <button
              type="button"
              onClick={() => setStep('verified')}
              className="w-full text-gray-500 text-sm"
            >
              ← Indietro
            </button>
          </form>
        )}
        
        {/* Footer */}
        <div className="mt-6 pt-4 border-t text-center">
          <button onClick={() => navigate('/')} className="text-gray-400 text-sm hover:text-gray-600">
            Torna alla Home
          </button>
        </div>
      </div>
    </div>
  );
};

// ATTIVA CODICE PAGE - Accesso diretto socio con codice
const AttivaCodicePage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Se il socio ha già un token valido, redirect diretto
  useEffect(() => {
    const token = localStorage.getItem('socio_token');
    if (token) navigate('/area-socio');
  }, [navigate]);

  const handleAccess = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError('Inserisci il codice');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/socio-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Codice non valido o revocato. Contatta il tuo amministratore.');
        setLoading(false);
        return;
      }

      // Salva token e dati socio
      localStorage.setItem('socio_token', data.token);
      localStorage.setItem('socio_info', JSON.stringify(data.socio));

      // Redirect diretto all'area personale
      navigate('/area-socio');
    } catch (err) {
      setError('Errore di connessione. Riprova.');
    }

    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleAccess();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full" data-testid="attiva-codice-page">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800" data-testid="page-title">Accedi al Club</h1>
          <p className="text-gray-500 mt-1">Inserisci il codice ricevuto dal tuo club</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="error-message">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <input
            type="text"
            placeholder="Es: GREEN-XK92"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
            onKeyPress={handleKeyPress}
            className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-center text-xl tracking-widest uppercase font-mono focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
            maxLength={12}
            autoFocus
            data-testid="code-input"
          />
          <button
            onClick={handleAccess}
            disabled={loading || !code.trim()}
            className="w-full bg-green-600 text-white py-4 rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg"
            data-testid="access-button"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                Accesso in corso...
              </span>
            ) : 'Accedi'}
          </button>
        </div>

        <div className="mt-6 pt-4 border-t text-center">
          <p className="text-gray-400 text-xs">Il codice viene fornito dall'amministratore del tuo club</p>
        </div>
      </div>
    </div>
  );
};

// AREA SOCIO - Dashboard personale accessibile con codice
const AreaSocioPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('stagione');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const token = localStorage.getItem('socio_token');
    if (!token) {
      navigate('/attiva-codice');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/socio/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.status === 401) {
        localStorage.removeItem('socio_token');
        localStorage.removeItem('socio_info');
        navigate('/attiva-codice');
        return;
      }

      const data = await res.json();
      if (data.success) {
        setProfile(data);
      } else {
        setError('Errore caricamento profilo');
      }
    } catch (err) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('socio_token');
    localStorage.removeItem('socio_info');
    navigate('/attiva-codice');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto" />
          <p className="mt-4 text-gray-600">Caricamento profilo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate('/attiva-codice')} className="px-4 py-2 bg-green-600 text-white rounded-lg">
            Torna all'accesso
          </button>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const { club, socio, my_stats, my_position, total_soci, classifiche } = profile;
  const stats = my_stats || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="area-socio">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-teal-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-200 text-sm">{club.name} {club.city && `- ${club.city}`}</p>
              <h1 className="text-2xl font-bold" data-testid="socio-name">{socio.nome}</h1>
            </div>
            <button
              onClick={handleLogout}
              className="text-green-200 hover:text-white text-sm flex items-center gap-1"
              data-testid="logout-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Card Posizione e Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-3xl font-bold text-green-600" data-testid="my-position">{my_position || '-'}</div>
            <div className="text-xs text-gray-500 mt-1">Posizione su {total_soci}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{stats.partite || 0}</div>
            <div className="text-xs text-gray-500 mt-1">Partite</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">{stats.vinte || 0}</div>
            <div className="text-xs text-gray-500 mt-1">Vittorie</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{stats.gol_fatti || 0}</div>
            <div className="text-xs text-gray-500 mt-1">Gol Fatti</div>
          </div>
        </div>

        {/* Stats Dettagliati */}
        {my_stats && (
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="font-bold text-gray-800 mb-4">Le Mie Statistiche</h2>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center text-sm">
              <div>
                <div className="font-bold text-lg text-gray-800">{stats.vinte || 0}</div>
                <div className="text-gray-500">V</div>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-800">{stats.pareggiate || 0}</div>
                <div className="text-gray-500">P</div>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-800">{stats.perse || 0}</div>
                <div className="text-gray-500">S</div>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-800">{stats.gol_fatti || 0}</div>
                <div className="text-gray-500">GF</div>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-800">{stats.gol_subiti || 0}</div>
                <div className="text-gray-500">GS</div>
              </div>
              <div>
                <div className="font-bold text-lg text-blue-600">{stats.punti_ranking_totali || 0}</div>
                <div className="text-gray-500">Ranking</div>
              </div>
            </div>
            {/* Forma recente */}
            {stats.forma?.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <p className="text-xs text-gray-500 mb-2">Forma recente</p>
                <div className="flex gap-1">
                  {stats.forma.map((r, i) => (
                    <span key={i} className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold ${
                      r === 'V' ? 'bg-green-500 text-white' : r === 'P' ? 'bg-gray-400 text-white' : 'bg-red-500 text-white'
                    }`}>{r}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Classifiche Club */}
        {(classifiche?.stagione?.length > 0 || classifiche?.gol?.length > 0) && (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="flex border-b">
              {[
                { id: 'stagione', label: 'Classifica Stagione' },
                { id: 'gol', label: 'Capocannonieri' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-green-50 text-green-700 border-b-2 border-green-600' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="p-4">
              {classifiche[activeTab]?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nessun dato disponibile</p>
              ) : (
                <div className="space-y-2">
                  {classifiche[activeTab]?.map((s, i) => {
                    const isMe = s.nome?.toUpperCase() === socio.nome?.toUpperCase() || s.socio_id === socio.anagrafica_id;
                    return (
                      <div key={s.socio_id || i} className={`flex items-center justify-between p-3 rounded-lg ${isMe ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            i === 0 ? 'bg-yellow-400 text-yellow-900' :
                            i === 1 ? 'bg-gray-300 text-gray-700' :
                            i === 2 ? 'bg-orange-300 text-orange-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>{s.pos}</span>
                          <span className={`font-medium ${isMe ? 'text-green-700 font-bold' : 'text-gray-800'}`}>
                            {s.nome} {isMe && '(tu)'}
                          </span>
                        </div>
                        <span className="font-bold text-blue-600">
                          {activeTab === 'stagione' ? `${s.punti_ranking_totali || 0} pt` : `${s.gol_fatti || 0} gol`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="text-center text-gray-400 text-xs py-4">
          {club.name} - My Subbuteo Manager
        </div>
      </main>
    </div>
  );
};

// 🌐 PUBLIC PROFILE PAGE - Profilo pubblico utente singolo
const PublicProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [instagramCopied, setInstagramCopied] = useState(false);

  useEffect(() => {
    if (username) loadPublicProfile();
  }, [username]);

  const loadPublicProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/profilo/${username}`);
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else if (response.status === 403) {
        setError('Questo profilo è privato');
      } else if (response.status === 404) {
        setError('Utente non trovato');
      } else {
        setError('Errore nel caricamento');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  // Generate social share URL with dynamic OG tags
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const socialShareUrl = `${baseUrl}/api/s/profilo/${username}`;

  const handleShare = async (platform) => {
    const text = `Guarda le statistiche di ${profileData?.user?.name} su My Subbuteo Manager!`;
    // Use social URL for sharing (has OG meta tags)
    const urlToShare = socialShareUrl;
    
    switch(platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + urlToShare)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(urlToShare)}&quote=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'instagram':
        try {
          await navigator.clipboard.writeText(urlToShare);
        } catch (e) {
          // Fallback se clipboard non disponibile
          console.log('Clipboard non disponibile');
        }
        setInstagramCopied(true);
        setTimeout(() => setInstagramCopied(false), 3000);
        // Apri Instagram se su mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          setTimeout(() => {
            window.open('instagram://app', '_blank');
          }, 500);
        }
        break;
      case 'copy':
        navigator.clipboard.writeText(urlToShare);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-400">Caricamento profilo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="text-center">
          <div className="text-6xl mb-4">{error.includes('privato') ? '🔒' : '🚫'}</div>
          <h1 className="text-2xl font-bold text-white mb-2">{error}</h1>
          <p className="text-gray-400 mb-6">
            {error.includes('privato') ? 'L\'utente ha scelto di mantenere il profilo privato.' : 'Controlla l\'URL e riprova.'}
          </p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }

  if (!profileData) return null;

  const { user, stats, highlights, recent_form, best_opponent, nemesis } = profileData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-emerald-700 shadow-2xl">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-5">
            {/* Avatar */}
            <div className="relative">
              {user.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt={user.name}
                  className="w-24 h-24 rounded-full border-4 border-white/30 shadow-xl object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-white/20 border-4 border-white/30 flex items-center justify-center shadow-xl">
                  <span className="text-4xl">⚽</span>
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                🌐 Pubblico
              </div>
            </div>
            
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold text-white">{user.name}</h1>
              {user.member_since && (
                <p className="text-green-200 text-sm mt-1">🗓️ Membro dal {user.member_since}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        
        {/* Stats principali */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-5 text-center border border-gray-700/50">
            <div className="text-4xl font-bold text-white">{stats.total_matches}</div>
            <div className="text-gray-400 text-sm mt-1">Partite</div>
          </div>
          <div className="bg-gradient-to-br from-green-600/30 to-green-800/30 backdrop-blur rounded-2xl p-5 text-center border border-green-500/30">
            <div className="text-4xl font-bold text-green-400">{stats.wins}</div>
            <div className="text-green-300 text-sm mt-1">Vittorie</div>
            <div className="text-green-400 font-bold text-lg">{stats.win_percentage}%</div>
          </div>
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-5 text-center border border-gray-700/50">
            <div className="text-4xl font-bold text-white">{stats.goals_scored}</div>
            <div className="text-gray-400 text-sm mt-1">Gol Fatti</div>
          </div>
        </div>

        {/* V/P/S Dettaglio */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-gray-700/50">
          <h3 className="text-white font-bold mb-4">📊 Riepilogo</h3>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-400">{stats.wins}</div>
              <div className="text-gray-400 text-xs">Vittorie</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-400">{stats.draws}</div>
              <div className="text-gray-400 text-xs">Pareggi</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-400">{stats.losses}</div>
              <div className="text-gray-400 text-xs">Sconfitte</div>
            </div>
            <div>
              <div className={`text-2xl font-bold ${stats.goals_diff >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.goals_diff >= 0 ? '+' : ''}{stats.goals_diff}
              </div>
              <div className="text-gray-400 text-xs">Diff. Gol</div>
            </div>
          </div>
          
          {/* Streak */}
          {stats.current_streak > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center justify-center gap-2">
                <span className={`text-2xl ${stats.streak_type === 'win' ? '🔥' : stats.streak_type === 'loss' ? '❄️' : '➡️'}`}>
                  {stats.streak_type === 'win' ? '🔥' : stats.streak_type === 'loss' ? '❄️' : '➡️'}
                </span>
                <span className="text-white font-medium">
                  {stats.current_streak} {stats.streak_type === 'win' ? 'vittorie' : stats.streak_type === 'loss' ? 'sconfitte' : 'pareggi'} consecutive
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Highlights */}
        {highlights && highlights.length > 0 && (
          <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 backdrop-blur rounded-2xl p-6 border border-yellow-500/30">
            <h3 className="text-yellow-400 font-bold mb-4">⭐ Highlights</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-3 bg-black/20 rounded-xl px-4 py-3">
                  <span className="text-2xl">{h.icon}</span>
                  <span className="text-white text-sm">{h.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Forma recente */}
        {recent_form && recent_form.length > 0 && (
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-gray-700/50">
            <h3 className="text-white font-bold mb-4">📈 Forma Recente</h3>
            <div className="flex justify-center gap-2 mb-4">
              {recent_form.map((m, i) => (
                <div 
                  key={i}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    m.outcome === 'win' ? 'bg-green-500 text-white' : 
                    m.outcome === 'loss' ? 'bg-red-500 text-white' : 
                    'bg-gray-500 text-white'
                  }`}
                  title={`vs ${m.opponent}: ${m.result}`}
                >
                  {m.outcome === 'win' ? 'V' : m.outcome === 'loss' ? 'S' : 'P'}
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {recent_form.map((m, i) => (
                <div key={i} className="flex justify-between items-center text-sm bg-black/20 rounded-lg px-4 py-2">
                  <span className="text-gray-400">{m.date}</span>
                  <span className="text-white">vs {m.opponent}</span>
                  <span className={`font-bold ${
                    m.outcome === 'win' ? 'text-green-400' : 
                    m.outcome === 'loss' ? 'text-red-400' : 
                    'text-gray-400'
                  }`}>{m.result}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Best Opponent / Nemesis */}
        {(best_opponent || nemesis) && (
          <div className="grid md:grid-cols-2 gap-4">
            {best_opponent && (
              <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 backdrop-blur rounded-2xl p-5 border border-green-500/30">
                <div className="text-3xl mb-2">🎯</div>
                <div className="text-green-300 text-sm">Avversario preferito</div>
                <div className="text-white font-bold text-xl">{best_opponent}</div>
              </div>
            )}
            {nemesis && (
              <div className="bg-gradient-to-br from-red-600/20 to-rose-600/20 backdrop-blur rounded-2xl p-5 border border-red-500/30">
                <div className="text-3xl mb-2">😈</div>
                <div className="text-red-300 text-sm">Nemesi</div>
                <div className="text-white font-bold text-xl">{nemesis}</div>
              </div>
            )}
          </div>
        )}

        {/* Pulsanti Social */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl p-6 border border-gray-700/50">
          <h3 className="text-white font-bold mb-4 text-center">📤 Condividi questo profilo</h3>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => handleShare('whatsapp')}
              className="flex items-center gap-2 px-5 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-all shadow-lg"
              data-testid="share-whatsapp"
            >
              <span className="text-xl">📱</span> WhatsApp
            </button>
            <button
              onClick={() => handleShare('facebook')}
              className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all shadow-lg"
              data-testid="share-facebook"
            >
              <span className="text-xl">📘</span> Facebook
            </button>
            <button
              onClick={() => handleShare('instagram')}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all shadow-lg ${
                instagramCopied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
              }`}
              data-testid="share-instagram"
            >
              <span className="text-xl">{instagramCopied ? '✅' : '📸'}</span> 
              {instagramCopied ? 'Link copiato!' : 'Instagram'}
            </button>
            <button
              onClick={() => handleShare('copy')}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all shadow-lg ${
                copied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-600 text-white hover:bg-gray-500'
              }`}
              data-testid="share-copy"
            >
              <span className="text-xl">{copied ? '✅' : '🔗'}</span> 
              {copied ? 'Copiato!' : 'Copia Link'}
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-6 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Vuoi tracciare anche le tue partite?</h3>
          <p className="text-green-200 mb-4">Registrati gratuitamente e inizia a monitorare le tue statistiche</p>
          <button 
            onClick={() => navigate('/registrati')}
            className="px-8 py-3 bg-white text-green-700 font-bold rounded-xl hover:bg-green-50 transition-all shadow-lg"
          >
            Registrati Gratis
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">My Subbuteo Manager - Il gestionale digitale per il Subbuteo</p>
        </div>
      </footer>
    </div>
  );
};

// 📖 GUIDA PAGE - Documentazione completa dell'app
const GuidaPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('intro');
  
  const sections = [
    { id: 'intro', icon: '👋', title: 'Introduzione' },
    { id: 'club', icon: '🏢', title: 'Per i Club' },
    { id: 'giocatori', icon: '👤', title: 'Per i Giocatori' },
    { id: 'tornei', icon: '🏆', title: 'Gestione Tornei' },
    { id: 'bacheca', icon: '📢', title: 'Bacheca & Comunicazioni' },
    { id: 'statistiche', icon: '📊', title: 'Statistiche' },
    { id: 'faq', icon: '❓', title: 'FAQ' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600">
            <span>←</span> Torna alla Home
          </button>
          <h1 className="text-xl font-bold text-blue-600">📖 Guida</h1>
          <div className="flex gap-2">
            <button onClick={() => navigate('/login')} className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm">
              Accedi
            </button>
            <button onClick={() => navigate('/login-club')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              Area Club
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            📖 Guida Completa
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Tutto quello che devi sapere per utilizzare My Subbuteo Manager al meglio
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <nav className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-lg p-4 sticky top-24">
              <h3 className="font-bold text-gray-700 mb-4 px-2">Sezioni</h3>
              <div className="space-y-1">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => {
                      setActiveSection(section.id);
                      document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                      activeSection === section.id 
                        ? 'bg-blue-100 text-blue-700 font-medium' 
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <span>{section.icon}</span>
                    <span>{section.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1 space-y-12">
            
            {/* INTRO */}
            <section id="intro" className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="text-3xl">👋</span> Benvenuto su My Subbuteo Manager
              </h2>
              <div className="prose max-w-none text-gray-600 space-y-4">
                <p className="text-lg">
                  <strong>My Subbuteo Manager</strong> è il gestionale completo per i club di Subbuteo. 
                  Gestisci tornei, soci, statistiche e comunicazioni in un'unica piattaforma.
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">🏢 Sei un Club?</h4>
                    <p className="text-sm text-blue-700">Registra il tuo club, gestisci i soci, organizza tornei e tieni traccia di tutte le statistiche.</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-bold text-green-800 mb-2">👤 Sei un Giocatore?</h4>
                    <p className="text-sm text-green-700">Tieni traccia delle tue partite, visualizza le tue statistiche e partecipa ai tornei del tuo club.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CLUB */}
            <section id="club" className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="text-3xl">🏢</span> Guida per i Club
              </h2>
              <div className="space-y-6 text-gray-600">
                <div>
                  <h3 className="font-bold text-lg mb-2">Come registrare il club</h3>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>Vai su "Area Club" dalla homepage</li>
                    <li>Clicca su "Registra Club"</li>
                    <li>Compila i dati del club (nome, città, descrizione)</li>
                    <li>Crea le credenziali di accesso</li>
                  </ol>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">Gestione Soci</h3>
                  <p>Dalla dashboard puoi aggiungere soci, generare codici di invito e gestire i permessi.</p>
                </div>
              </div>
            </section>

            {/* GIOCATORI */}
            <section id="giocatori" className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="text-3xl">👤</span> Guida per i Giocatori
              </h2>
              <div className="space-y-6 text-gray-600">
                <div>
                  <h3 className="font-bold text-lg mb-2">Come entrare in un club</h3>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>Richiedi un codice di invito all'admin del tuo club</li>
                    <li>Vai su "Attiva Codice" dalla homepage</li>
                    <li>Inserisci il codice e completa la registrazione</li>
                  </ol>
                </div>
              </div>
            </section>

            {/* TORNEI */}
            <section id="tornei" className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="text-3xl">🏆</span> Gestione Tornei
              </h2>
              <div className="space-y-6 text-gray-600">
                <p>Il sistema supporta diversi formati di torneo:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Girone italiano</strong>: Round robin classico</li>
                  <li><strong>Eliminazione diretta</strong>: Bracket ad eliminazione</li>
                  <li><strong>Gironi + Fasce</strong>: Sistema avanzato con qualificazioni</li>
                </ul>
              </div>
            </section>

            {/* BACHECA */}
            <section id="bacheca" className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="text-3xl">📢</span> Bacheca & Comunicazioni
              </h2>
              <div className="text-gray-600">
                <p>La bacheca è lo spazio per comunicare con tutti i soci del club. Pubblica annunci, eventi e aggiornamenti.</p>
              </div>
            </section>

            {/* STATISTICHE */}
            <section id="statistiche" className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="text-3xl">📊</span> Statistiche
              </h2>
              <div className="text-gray-600">
                <p>Il sistema calcola automaticamente statistiche dettagliate per ogni giocatore e torneo, inclusi:</p>
                <ul className="list-disc list-inside mt-4 space-y-2">
                  <li>Percentuale vittorie</li>
                  <li>Gol fatti e subiti</li>
                  <li>Punti ranking</li>
                  <li>Forma recente</li>
                </ul>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="text-3xl">❓</span> FAQ
              </h2>
              <div className="space-y-4 text-gray-600">
                <div>
                  <h4 className="font-bold">Come recupero la password?</h4>
                  <p className="text-sm">Contatta l'admin del tuo club o usa la funzione "Password dimenticata".</p>
                </div>
                <div>
                  <h4 className="font-bold">Posso far parte di più club?</h4>
                  <p className="text-sm">Sì, puoi essere membro di più club contemporaneamente.</p>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-400">My Subbuteo Manager - Il gestionale digitale per il Subbuteo</p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white">Home</button>
            <button onClick={() => navigate('/login')} className="text-gray-400 hover:text-white">Accedi</button>
            <button onClick={() => navigate('/login-club')} className="text-gray-400 hover:text-white">Area Club</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export { PublicClubPage, AttivaCodicePage, AreaSocioPage, GuidaPage, PublicProfilePage };
